import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { MenuItem } from '../types';
import { trackClickMenuItem } from '../analytics/analytics';

interface MenuItemCardProps {
  item: MenuItem;
  onViewDetails?: (item: MenuItem) => void;
  readOnly?: boolean;
}

// Imágenes por defecto atractivas para diferentes tipos de comida
const defaultFoodImages = [
  '🍕', '🍔', '🥗', '🍜', '🍣', '🥩', '🍗', '🐟', '🍤', '🥘', 
  '🍲', '🥣', '🥙', '🌮', '🌯', '🥪', '🥐', '🍞', '🍰', '🍮', 
  '🍦', '🍨', '🍩', '🍪', '🍫', '🍬', '🍭', '🍯', '☕', '🍵', 
  '🥤', '🍺', '🍷', '🍸', '🍹', '🥂', '🥃', '🍾'
];

// Función para obtener una imagen por defecto basada en el nombre del plato
const getDefaultFoodEmoji = (itemName: string): string => {
  const name = itemName.toLowerCase();
  
  // Mapeo específico por tipo de comida
  if (name.includes('pizza') || name.includes('pizz')) return '🍕';
  if (name.includes('hamburguesa') || name.includes('burger') || name.includes('hamburger')) return '🍔';
  if (name.includes('ensalada') || name.includes('salad')) return '🥗';
  if (name.includes('pasta') || name.includes('spaghetti') || name.includes('fettuccine')) return '🍜';
  if (name.includes('sushi') || name.includes('maki') || name.includes('roll')) return '🍣';
  if (name.includes('carne') || name.includes('steak') || name.includes('beef')) return '🥩';
  if (name.includes('pollo') || name.includes('chicken')) return '🍗';
  if (name.includes('pescado') || name.includes('fish') || name.includes('salmon')) return '🐟';
  if (name.includes('camarón') || name.includes('shrimp') || name.includes('prawn')) return '🍤';
  if (name.includes('sopa') || name.includes('soup')) return '🥘';
  if (name.includes('taco') || name.includes('burrito')) return '🌮';
  if (name.includes('sandwich') || name.includes('sándwich')) return '🥪';
  if (name.includes('pan') || name.includes('bread')) return '🍞';
  if (name.includes('postre') || name.includes('dessert') || name.includes('cake')) return '🍰';
  if (name.includes('helado') || name.includes('ice cream')) return '🍦';
  if (name.includes('café') || name.includes('coffee')) return '☕';
  if (name.includes('té') || name.includes('tea')) return '🍵';
  if (name.includes('bebida') || name.includes('drink') || name.includes('jugo')) return '🥤';
  if (name.includes('cerveza') || name.includes('beer')) return '🍺';
  if (name.includes('vino') || name.includes('wine')) return '🍷';
  if (name.includes('cocktail') || name.includes('cóctel')) return '🍸';
  
  // Si no coincide con nada específico, usar un emoji aleatorio basado en el hash del nombre
  const hash = itemName.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);

  return defaultFoodImages[Math.abs(hash) % defaultFoodImages.length];
};

// Función para obtener el color de fondo basado en el emoji
const getBackgroundColorForEmoji = (emoji: string): string => {
  const colorMap: { [key: string]: string } = {
    '🍕': '#FFE4E1', // Rosa claro para pizza
    '🍔': '#FFF8DC', // Amarillo claro para hamburguesas
    '🥗': '#F0FFF0', // Verde claro para ensaladas
    '🍜': '#FFF5EE', // Naranja claro para pasta
    '🍣': '#F0F8FF', // Azul claro para sushi
    '🥩': '#FFE4E1', // Rosa claro para carnes
    '🍗': '#FFF8DC', // Amarillo claro para pollo
    '🐟': '#E6F3FF', // Azul muy claro para pescado
    '🍤': '#FFF0F5', // Rosa muy claro para mariscos
    '🥘': '#FFF5EE', // Naranja claro para sopas
    '🌮': '#FFFACD', // Amarillo claro para tacos
    '🥪': '#F5F5DC', // Beige claro para sandwiches
    '🍞': '#FFF8DC', // Amarillo claro para pan
    '🍰': '#FFF0F5', // Rosa muy claro para postres
    '🍦': '#F0F8FF', // Azul claro para helado
    '☕': '#F5F5DC', // Beige claro para café
    '🍵': '#F0FFF0', // Verde claro para té
    '🥤': '#E6F3FF', // Azul muy claro para bebidas
    '🍺': '#FFFACD', // Amarillo claro para cerveza
    '🍷': '#FFE4E1', // Rosa claro para vino
    '🍸': '#FFF0F5', // Rosa muy claro para cócteles
  };
  
  return colorMap[emoji] || '#F8F9FA'; // Gris claro por defecto
};

export default function MenuItemCard({ item, onViewDetails, readOnly = false }: MenuItemCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, isOwner, isManager } = useAuth();
  
  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const getStatusColor = () => {
    return item.available ? '#4ADE80' : '#FCA5A5';
  };

  // Obtener la imagen principal del menú
  const getMainImage = () => {
    // Si hay imágenes del backend, usar la primera
    if (item.images && item.images.length > 0) {
      // Manejar tanto strings como objetos MenuImage
      const firstImage = item.images[0];
      const imageUrl = typeof firstImage === 'string' ? firstImage : firstImage.imageUrl;
      if (imageUrl) {
        return { type: 'backend', imageUrl };
      }
    }
    
    // Si no hay imágenes, usar emoji por defecto
    return { type: 'emoji', emoji: getDefaultFoodEmoji(item.name) };
  };

  const mainImage = getMainImage();

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const isPlanLimited = item.status === 'plan_limited' || (typeof item.status === 'string' && item.status.toLowerCase().includes('plan'));
  const canPromptPay = isPlanLimited && isAuthenticated && (isOwner() || isManager());

  return (
    <div 
      className={`bg-white rounded-2xl shadow-lg overflow-hidden relative flex flex-col min-h-[240px] cursor-pointer hover:shadow-xl transition-shadow ${isPlanLimited ? 'opacity-60 saturate-50 select-none' : ''}`}
      onClick={() => {
        trackClickMenuItem(item.name, item.id, item.categoryName || item.category);
        onViewDetails?.(item);
      }}
    >
      {/* Imagen principal */}
      <div className="relative mb-3 px-3 pt-3">
        {mainImage.type === 'emoji' ? (
          <div 
            className="w-full h-24 rounded-xl flex items-center justify-center text-3xl font-bold"
            style={{ backgroundColor: getBackgroundColorForEmoji(mainImage.emoji || '') }}
          >
            {mainImage.emoji}
          </div>
        ) : (
          <div className="relative w-full h-24 rounded-xl overflow-hidden">
            {/* Skeleton loader mientras la imagen carga */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
              </div>
            )}
            
            {/* Imagen real */}
            <img
              src={mainImage.imageUrl}
              alt={item.name}
              className={`w-full h-24 rounded-xl object-cover transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={handleImageLoad}
              onError={() => setImageLoaded(true)} // En caso de error, mostrar el emoji por defecto
            />
          </div>
        )}
        
        {/* Badge de estado */}
        <div 
          className="absolute top-2 right-2 px-2 py-1 rounded-lg text-xs font-bold text-white"
          style={{ backgroundColor: getStatusColor() }}
        >
          {isPlanLimited ? 'Limitado por plan' : (item.available ? 'Disponible' : 'No disponible')}
        </div>

        {/* Precio en la imagen */}
        <div className="absolute bottom-2 right-2 bg-black bg-opacity-80 px-2 py-1 rounded-xl">
          <span className="text-sm font-bold text-white">{formatPrice(item.price)}</span>
        </div>

        {/* Indicador de múltiples imágenes */}
        {item.images && item.images.length > 1 && (
          <div className="absolute top-2 left-2 bg-black bg-opacity-50 rounded-lg px-2 py-1">
            <span className="text-xs font-bold text-white">+{item.images.length - 1}</span>
          </div>
        )}
      </div>

      {/* Contenido principal */}
      <div className="flex-1 px-3 pb-3">
        {/* Nombre del platillo */}
        <h3 className="text-sm font-bold text-[#2C4358] mb-2 leading-5 line-clamp-2">
          {item.name}
        </h3>

        {/* Descripción */}
        <p className="text-xs text-[#85929e] mb-2 leading-4 line-clamp-2">
          {item.description}
        </p>
        
        {/* Información adicional */}
        <div className="mb-2">
          {(item.categoryIcon || item.categoryName || item.preparationTime) && (
            <div className="flex items-center justify-between mb-1 gap-2 min-w-0">
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <span className="text-sm flex-shrink-0">{item.categoryIcon || '🍽️'}</span>
                {item.categoryName && (
                  <span className="text-xs text-[#85929e] font-medium truncate">
                    {item.categoryName}
                  </span>
                )}
              </div>
              {item.preparationTime && (
                <div className="flex items-center flex-shrink-0">
                  <span className="text-sm mr-1">⏱️</span>
                  <span className="text-xs text-[#85929e] font-medium">{item.preparationTime} min</span>
                </div>
              )}
            </div>
          )}
          
          {/* Alérgenos */}
          {item.allergens && item.allergens.length > 0 && (
            <div className="mb-2">
              <div className="flex items-center mb-1">
                <span className="text-xs font-semibold text-[#85929e] mr-1">⚠️</span>
                <div className="flex flex-wrap gap-1">
                  {item.allergens.slice(0, 2).map((allergen, index) => (
                    <span 
                      key={index} 
                      className="bg-gray-100 px-1.5 rounded-md border border-gray-200"
                    >
                      <span className="text-xs text-gray-700 font-medium">{allergen}</span>
                    </span>
                  ))}
                  {item.allergens.length > 2 && (
                    <span className="text-xs text-[#85929e] italic">+{item.allergens.length - 2}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botón flotante de acción (oculto en solo lectura) */}
        {!readOnly && (
          <button
            className={`absolute bottom-2 right-2 w-9 h-9 rounded-full flex items-center justify-center font-bold text-xl shadow-lg transition-colors border-2 ${
              item.available 
                ? 'bg-[#f7ebd6] text-[#2C4358] hover:bg-[#f0e0c0] border-[#f7ebd6]/50 hover:border-[#f0e0c0]' 
                : 'bg-[#6b7280] text-white cursor-not-allowed border-[#6b7280]/50'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (item.available) {
                onViewDetails?.(item);
              }
            }}
            disabled={!item.available || isPlanLimited}
          >
            {isPlanLimited ? '🔒' : (item.available ? '+' : '×')}
          </button>
        )}
      </div>
      {isPlanLimited && (
        <div className="absolute inset-0 backdrop-blur-[2px]" />
      )}
      {canPromptPay && (
        <div className="absolute inset-0 flex items-center justify-center p-3">
          <div className="bg-white/95 backdrop-blur-md border border-[#22B6B2]/30 rounded-2xl shadow-2xl px-4 py-3 text-center">
            <div className="text-sm font-bold text-[#2C4358] mb-2">Funcionalidad limitada por plan</div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/pricing'); }}
              className="px-3 py-2 rounded-xl bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white text-sm font-bold hover:opacity-90 transition"
            >
              Actualizar plan
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 